import React, { useState, useEffect } from "react";
import { Brain } from "lucide-react";

const EmailSummaryModal = ({ isOpen, onClose, email }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && email) {
      setLoading(true);
      setSummaryData(null);
      
      fetch("/api/mail/summarize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_id: email.id,
          content: email.full_content || email.content || email.snippet || "",
          subject: email?.subject || "",
          sender: email.sender || ""
        })
      })
      .then(response => response.json())
      .then(data => {
        setSummaryData(data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error summarizing email:", error);
        setSummaryData({ error: "Summarization failed" });
        setLoading(false);
      });
    }
  }, [isOpen, email]);

  if (!isOpen) return null;

  const formatConversationAnalysis = (data) => {
    if (data.error) return `❌ Zusammenfassung nicht verfügbar: ${data.error}`;
    
    let displayText = "🧠 KI-MAIL-ANALYSE\n═══════════════════════════════════════\n\n";
    
    // Main conversation summary - this is the intelligent analysis
    const summary = data.detailed_analysis?.full_summary || data.summary || "";
    if (summary) {
      displayText += summary + "\n\n";
    }
    
    // Additional metadata (non-redundant)
    displayText += `🏷️ KATEGORIE: ${data.category || "Unbekannt"} (${Math.round((data.category_confidence || 0) * 100)}% Konfidenz)\n`;
    displayText += `⚡ PRIORITÄT: ${data.priority || "Unbekannt"}\n`;
    
    // Only show business value if it adds new information
    if (data.detailed_analysis?.business_value && !summary.includes(data.detailed_analysis.business_value)) {
      displayText += `💼 GESCHÄFTSWERT: ${data.detailed_analysis.business_value}\n`;
    }
    
    displayText += "\n";
    
    // Show extracted entities only if they contain useful data
    if (data.detailed_analysis?.extracted_entities) {
      const entities = data.detailed_analysis.extracted_entities;
      let hasEntities = false;
      let entitiesText = "🔍 ERKANNTE DATEN:\n";
      
      if (entities.companies?.length > 0) {
        entitiesText += `🏢 Unternehmen: ${entities.companies.slice(0, 3).join(", ")}\n`;
        hasEntities = true;
      }
      if (entities.amounts?.length > 0) {
        entitiesText += `💰 Beträge: ${entities.amounts.slice(0, 3).join(", ")}\n`;
        hasEntities = true;
      }
      if (entities.locations?.length > 0) {
        entitiesText += `📍 Orte: ${entities.locations.slice(0, 3).join(", ")}\n`;
        hasEntities = true;
      }
      if (entities.contacts?.length > 0) {
        entitiesText += `👤 Kontakte: ${entities.contacts.slice(0, 3).join(", ")}\n`;
        hasEntities = true;
      }
      
      if (hasEntities) {
        displayText += entitiesText + "\n";
      }
    }
    
    displayText += `═══════════════════════════════════════\n⏰ Analysiert am ${new Date().toLocaleString("de-DE")}`;
    return displayText;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-3/4 h-3/4 max-w-4xl max-h-screen overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
            <Brain className="w-6 h-6 mr-2 text-blue-600" />
            KI Mail-Analyse
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
          >
            ×
          </button>
        </div>
        <div className="p-6 overflow-y-auto h-full">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Analysiere E-Mail...</p>
              </div>
            </div>
          ) : summaryData ? (
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono leading-relaxed">
              {formatConversationAnalysis(summaryData)}
            </pre>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Keine Daten verfügbar</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSummaryModal;
